import React, { Fragment, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router-dom";
import Select from "react-select";
import { CircularProgress } from "@material-ui/core";
import { REQSendPushNoti, CleanUpPushNoti } from "./redux/actions";
import DevicePreview from "./DevicePreview";
import {
  initialForm,
  buildRequests,
  buildTargets,
  validateForm,
  MIN_SCHEDULE_LEAD_MINUTES
} from "./payload";
import { KNOWN_DATA_TYPES, PUSH_SEND_URL } from "./config";
import {
  buildNotificationPayload,
  formFromNotification,
  notificationFieldErrors,
  validateNotificationForm
} from "./payload";
import {
  REQNotificationDetail,
  SAVExNotification,
  PREVIEWxAudience,
  CleanUpNotificationDetail
} from "../NotificationDetail/redux/actions";
import {
  STATUS_OPTIONS,
  CONTENT_TYPE_OPTIONS,
  NOTIFICATION_TYPE_OPTIONS,
  ACTION_TYPE_OPTIONS,
  USER_SCOPE_OPTIONS,
  SURVEY_OPTIONS
} from "../NotificationList/api";
import { saveCampaign, makeCampaignId } from "../PushNotiList/history";
import Icon from "../../components/Icon";
import ConfirmDialog from "../../components/ConfirmDialog";
import "./style.scss";

// react-select dropdown styled like the NAV page (basic-single / select prefix), but with the
// native <select> ergonomics: a plain string value and an onChange(value) callback.
function FormSelect({ inputId, value, options, onChange, disabled, placeholder, invalid }) {
  return (
    <Select
      inputId={inputId}
      className={invalid ? "basic-single push-noti-select-invalid" : "basic-single"}
      classNamePrefix="select"
      isSearchable={false}
      isDisabled={disabled}
      placeholder={placeholder}
      options={options}
      value={options.find(option => option.value === value) || null}
      onChange={option => onChange(option ? option.value : "")}
    />
  );
}

// Every confirm / alert on this page is the shared ConfirmDialog (the "Discard unsaved changes?"
// card): one size, a left-aligned sentence-case heading, and a right-aligned left-to-right button
// row with the main action marked primary.
const ComposeDialog = ConfirmDialog;

const DATA_TYPE_OPTIONS = [
  { value: "", label: "— Unspecified —" },
  ...KNOWN_DATA_TYPES.map(type => ({ value: type, label: type }))
];

const PRIORITY_OPTIONS = [
  { value: "high", label: "high" },
  { value: "normal", label: "normal" }
];

// The button label is kept short so it fits the push UI; the field caps input and shows a counter.
const ACTION_LABEL_MAX = 40;

// The message is capped to match the Firebase console's own 1000-character limit on notification text.
const MESSAGE_MAX = 1000;

// Scheduling uses two native inputs (date + time), like Figma, over one Date value.
const pad2 = n => String(n).padStart(2, "0");
const toDateInput = dt => (dt ? `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}` : "");
const toTimeInput = dt => (dt ? `${pad2(dt.getHours())}:${pad2(dt.getMinutes())}` : "");
const combineSchedule = (dateStr, timeStr) => {
  if (!dateStr) {
    return null;
  }
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = (timeStr || "09:00").split(":").map(Number);
  return new Date(y, (m || 1) - 1, d, hh || 0, mm || 0);
};

const STEPS = [
  { no: 1, title: "Notification", en: "Notification" },
  { no: 2, title: "Target", en: "Target" },
  { no: 3, title: "Scheduling", en: "Scheduling" },
  { no: 4, title: "Conversion events", en: "Conversion events", optional: true },
  { no: 5, title: "Additional options", en: "Additional options", optional: true }
];

/*
 * Which step each field lives on, top-to-bottom, so a validation error can jump to the input:
 * switch to its step, then scroll it into view and focus it. Order here is the order fields are
 * checked for "the first thing to fix".
 */
const FIELD_STEP = [
  { field: "title", step: 1 },
  { field: "body", step: 1 },
  { field: "imageUrl", step: 1 },
  { field: "campaignName", step: 1 },
  { field: "actionLabel", step: 1 },
  { field: "actionLink", step: 1 },
  { field: "scopeEmails", step: 2 },
  { field: "scopeCustomerUuids", step: 2 },
  { field: "scopeAgeMax", step: 2 },
  { field: "scheduledAt", step: 3 }
];

/*
 * CSV import for the "by email" / "by customer uuid" scopes.
 *
 * Operators keep these lists in a spreadsheet, so rather than pasting hundreds of rows by hand
 * they can upload the exported CSV. We read the first column of every row, drop a header row when
 * it is one of the known keywords, and drop blank lines. The sample files below match this shape,
 * so an operator can download one, fill it in, and upload it back.
 */
const CSV_HEADER_KEYWORDS = ["email", "emails", "e-mail", "customer_uuid", "customeruuid", "uuid", "customer uuid"];

const SAMPLE_EMAIL_CSV = "email\nsomeone@example.com\nanother@example.com\n";

const SAMPLE_CUSTOMER_CSV =
  "customer_uuid\n404d1125-0e0c-4bba-883d-964d74fc731a\n7b3f2c1a-2c2c-4c2c-8c2c-964d74fc731a\n";

const parseCsvColumn = text =>
  String(text || "")
    .split(/\r?\n/)
    .map(line => (line.split(",")[0] || "").trim().replace(/^"|"$/g, ""))
    .filter((value, index) => value && !(index === 0 && CSV_HEADER_KEYWORDS.includes(value.toLowerCase())));

/*
 * The two things this page can send.
 *
 * They are different back ends, not two flavours of one: the notification centre resolves its own
 * audience from customer data and leaves an inbox row that Reach and View are counted against,
 * while a Firebase send names a topic or a device and leaves nothing behind. The steps after this
 * choice differ accordingly - conversion events and the raw FCM options only mean anything to a
 * Firebase send.
 */
const DELIVERY_MODES = [
  {
    value: "CUSTOMER",
    label: "In-app customer group",
    hint: "Also lands in the in-app inbox and counts Reach / View"
  }
  // Temporarily disabled: the Custom (Firebase) delivery mode is hidden for now.
  // Removing it leaves only the CUSTOMER flow (3 steps), hiding the Conversion
  // events / Additional options steps. Restore this entry to bring it back.
  // {
  //   value: "FIREBASE",
  //   label: "Custom (Firebase)",
  //   hint: "Sends directly to a topic or device, with no inbox history"
  // }
];

const TARGET_MODES = [
  { value: "ALL", label: "All users (topic ios / android)" },
  { value: "TOPIC", label: "Specify topic" },
  { value: "USER", label: "Specify users (customer uuid / unitholder id)" },
  { value: "TOKEN", label: "Specify devices (FCM registration token)" }
];

function PushNotiCompose(props) {
  const {
    REQSendPushNoti,
    CleanUpPushNoti,
    REQStrSendPushNoti,
    SendResults,
    SendSummary,
    REQNotificationDetail,
    SAVExNotification,
    PREVIEWxAudience,
    CleanUpNotificationDetail,
    Notification,
    Saving,
    Saved,
    EstimatedReach,
    notificationError,
    history,
    location
  } = props;

  // Editing an existing notification-centre record: /PushNoti_Compose?id=<uuid>
  const notificationId = new URLSearchParams(location.search).get("id");

  const oNeedAuth = JSON.parse(localStorage.getItem("NeedAuth"));

  // "Reuse" on the Campaign page hands the previous form over to be refilled
  const oReuse =
    history.location.state && history.location.state.campaign
      ? history.location.state.campaign.form
      : null;

  // "Send survey" on the CSAT / NPS page hands over a partial form set up for that survey
  // (title, internal name with today's date, content type Survey, and the survey itself).
  const oSurveyPrefill =
    history.location.state && history.location.state.surveyPrefill
      ? history.location.state.surveyPrefill
      : null;

  const [form, SETxForm] = useState(
    oReuse
      ? {
          ...initialForm,
          ...oReuse,
          scheduledAt: oReuse.scheduledAt ? new Date(oReuse.scheduledAt) : null
        }
      : oSurveyPrefill
      ? { ...initialForm, ...oSurveyPrefill }
      : initialForm
  );
  const [oStep, SETxStep] = useState(1);
  const [oVisited, SETxVisited] = useState([1]);
  const [oReview, SETxReview] = useState(false);
  // Per-field validation messages, shown under the input itself (keyed by form field).
  const [oFieldErrors, SETxFieldErrors] = useState({});
  // The field to scroll to / focus once its step is on screen after a failed validation.
  const oPendingFocus = useRef(null);
  const [oShwConfirm, SETxShwConfirm] = useState(false);
  const [oShwResult, SETxShwResult] = useState(false);
  const [oShwScheduled, SETxShwScheduled] = useState(false);
  const [oShwJson, SETxShwJson] = useState(false);
  // Cancel with unsaved edits asks before leaving (Figma "Discard unsaved changes?").
  const [oShwDiscard, SETxShwDiscard] = useState(false);
  // Snapshot of the form as last saved / loaded; anything different means unsaved edits.
  const oBaseline = useRef(null);

  useEffect(() => {
    if (notificationId) {
      REQNotificationDetail({ id: notificationId });
    }
    return () => {
      CleanUpPushNoti();
      CleanUpNotificationDetail();
    };
  }, [notificationId]);

  // The form as first shown is the baseline for detecting unsaved edits on Cancel.
  // Runs once on mount; `form` is intentionally read only for its initial value here.
  useEffect(() => {
    oBaseline.current = JSON.stringify(form);
  }, []);

  // Fill the form once the stored notification arrives, and reset the edit baseline to it.
  useEffect(() => {
    if (Notification && notificationId) {
      const filled = formFromNotification(Notification);
      SETxForm(filled);
      oBaseline.current = JSON.stringify(filled);
    }
  }, [Notification, notificationId]);

  // A saved notification belongs to the list, so that is where the operator lands.
  useEffect(() => {
    if (Saved) {
      history.push("/Notification_List");
    }
  }, [Saved]);

  useEffect(() => {
    if (SendSummary) {
      SETxShwResult(true);
    }
  }, [SendSummary]);

  // Once the offending field's step is rendered, bring it into view and focus it.
  useEffect(() => {
    const field = oPendingFocus.current;
    if (!field) {
      return undefined;
    }
    const timer = setTimeout(() => {
      const node = document.getElementById(`pn-${field}`);
      if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
        node.focus({ preventScroll: true });
      }
      oPendingFocus.current = null;
    }, 50);
    return () => clearTimeout(timer);
  }, [oStep, oFieldErrors]);

  // An error clears the moment the operator starts fixing that field.
  const clearFieldError = name =>
    SETxFieldErrors(prev => {
      if (!prev[name]) {
        return prev;
      }
      const next = { ...prev };
      delete next[name];
      return next;
    });

  /*
   * Show the field-level errors, jump to the first one (switching to its step), and return
   * whether the form was clean. Used by "Save draft" and "Review" in customer mode.
   */
  const showFieldErrors = () => {
    const fieldErrors = notificationFieldErrors(form);
    SETxFieldErrors(fieldErrors);
    const first = FIELD_STEP.find(entry => fieldErrors[entry.field]);
    if (first) {
      oPendingFocus.current = first.field;
      SETxStep(first.step);
      return false;
    }
    return true;
  };

  // The red message under an input, when that field failed validation.
  const RENDxFieldError = name =>
    oFieldErrors[name] ? <p className="push-noti-field-error">{oFieldErrors[name]}</p> : null;

  // Adds the error outline to an input's className when that field failed validation.
  const fieldClass = (name, base) => (oFieldErrors[name] ? `${base} push-noti-input-error` : base);

  const SETxField = name => ({ target: { value } }) => {
    clearFieldError(name);
    SETxForm(oldValues => ({ ...oldValues, [name]: value }));
  };

  const SETxValue = (name, value) => {
    clearFieldError(name);
    SETxForm(oldValues => ({ ...oldValues, [name]: value }));
  };

  /*
   * A Survey content type always carries a survey button, so choosing it selects the "Open survey"
   * action for the operator; that action is offered only then. Switching back to a general / promo /
   * urgent content type drops the survey action, so one of those can never keep a survey button.
   */
  const onContentTypeChange = value => {
    clearFieldError("actionLink");
    clearFieldError("actionLabel");
    SETxForm(oldValues => {
      const next = { ...oldValues, contentType: value };
      if (value === "SURVEY") {
        next.actionType = "SURVEY";
      } else if (oldValues.actionType === "SURVEY") {
        next.actionType = "NONE";
        next.actionLink = "";
        next.actionLabel = "";
      }
      return next;
    });
  };

  /*
   * Fill a scope textarea from an uploaded CSV. We reset the input value afterwards so choosing the
   * same file again still fires onChange, and replace (rather than append) so the textarea always
   * mirrors the file the operator just picked.
   */
  const IMPORTxCsv = field => event => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      SETxValue(field, parseCsvColumn(reader.result).join("\n"));
    };
    reader.readAsText(file);
  };

  const DOWNLOADxSample = (filename, content) => () => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const SETxPlatform = platform => () => {
    SETxForm(oldValues => ({
      ...oldValues,
      platforms: { ...oldValues.platforms, [platform]: !oldValues.platforms[platform] }
    }));
  };

  const SETxCustomData = (index, key, value) => {
    SETxForm(oldValues => {
      const customData = oldValues.customData.map((item, i) =>
        i === index ? { ...item, [key]: value } : item
      );
      return { ...oldValues, customData };
    });
  };

  const ADDxCustomData = () => {
    SETxForm(oldValues => ({
      ...oldValues,
      customData: [...oldValues.customData, { key: "", value: "" }]
    }));
  };

  const DELxCustomData = index => {
    SETxForm(oldValues => ({
      ...oldValues,
      customData: oldValues.customData.filter((item, i) => i !== index)
    }));
  };

  const GOTOxStep = next => {
    SETxStep(next);
    SETxReview(false);
    SETxVisited(oldValues =>
      oldValues.includes(next) ? oldValues : [...oldValues, next]
    );
  };

  const isCustomerMode = form.deliveryMode === "CUSTOMER";

  /*
   * A delivered notification is history: customers already have it in their inbox and may have
   * read it. Keyed on the send time rather than the status, like the backend guard - a campaign
   * whose pushes all failed was still delivered.
   */
  const readOnly = Boolean(notificationId && Notification && Notification.timeSent);

  // The Firebase steps are skipped entirely when the notification centre is doing the sending.
  const oSteps = isCustomerMode ? STEPS.slice(0, 3) : STEPS;

  const oTargets = buildTargets(form);
  const oRequests = buildRequests(form);

  /*
   * A step must be filled in before Next moves on, so an operator cannot skip ahead and reach
   * Review with an empty earlier step. We validate every field on this step and all before it
   * (never a later one, so Next never complains about a field the operator has not reached yet):
   * if one fails, show the errors, jump to the first, and stay put; otherwise advance.
   *
   * The Custom (Firebase) Target step picks a topic / token rather than the customer-scope fields,
   * so it is not in FIELD_STEP — guard its "at least 1 target" rule directly.
   */
  const tryGoNext = (fromStep, nextStep) => {
    const fieldErrors = notificationFieldErrors(form);
    if (!isCustomerMode && fromStep === 2 && oTargets.length === 0) {
      fieldErrors.target = "Select at least 1 target";
    }
    SETxFieldErrors(fieldErrors);

    const first = FIELD_STEP.find(entry => entry.step <= fromStep && fieldErrors[entry.field]);
    if (first) {
      oPendingFocus.current = first.field;
      SETxStep(first.step);
      return;
    }
    if (fieldErrors.target) {
      return;
    }
    GOTOxStep(nextStep);
  };

  const GENxCampaign = status => ({
    id: makeCampaignId(),
    name: form.campaignName || form.title || "(No campaign name)",
    title: form.title,
    body: form.body,
    imageUrl: form.imageUrl,
    targetMode: form.targetMode,
    targets: oTargets.map(target => target.label),
    scheduleMode: form.scheduleMode,
    scheduledAt: form.scheduledAt ? new Date(form.scheduledAt).toISOString() : null,
    createdAt: new Date().toISOString(),
    createdBy: oNeedAuth ? oNeedAuth.Email : "",
    endpoint: PUSH_SEND_URL,
    status,
    payload: oRequests.length > 0 ? oRequests[0].payload : null,
    form
  });

  const SAVExDraft = () => {
    if (isCustomerMode) {
      // The notification centre stores its own drafts, so this is a real record rather than a
      // campaign kept in this browser: status ACTIVE, nothing sent, editable from the list.
      // Show the message under each offending input and jump to the first one.
      if (!showFieldErrors()) {
        return;
      }
      SAVExNotification({ id: notificationId, body: buildNotificationPayload(form, false) });
      return;
    }
    saveCampaign(GENxCampaign("DRAFT"));
    history.push("/Notification_List");
  };

  // Unsaved edits exist when the form no longer matches the baseline snapshot.
  const isDirty = () =>
    oBaseline.current !== null && JSON.stringify(form) !== oBaseline.current;

  // Cancel leaves straight away when nothing changed; otherwise it confirms first.
  const requestCancel = () => {
    if (isDirty()) {
      SETxShwDiscard(true);
    } else {
      history.push("/Notification_List");
    }
  };

  // Belt-and-suspenders on top of CSS user-select:none — some browsers still let a click on
  // a radio / checkbox label select its text. Blocking the mousedown default on the label
  // (never on the input) stops the selection from starting while the click still toggles.
  const NOxLabelTextSelection = e => {
    const target = e.target;
    if (target && typeof target.closest === "function" && target.closest(".push-noti-radio, .push-noti-checkbox")) {
      // Never block the input itself (that would swallow the toggle); for the label / span,
      // stop the selection from starting.
      if (target.tagName !== "INPUT") {
        e.preventDefault();
      }
      // Whichever part was clicked, wipe any selection that a previous / double click may
      // have already dropped on the label so no blue highlight is ever left behind.
      const selection = typeof window !== "undefined" && window.getSelection ? window.getSelection() : null;
      if (selection && selection.rangeCount > 0) {
        selection.removeAllRanges();
      }
    }
  };

  const PREVIEWxReach = () => {
    const payload = buildNotificationPayload(form, false);
    /*
     * The estimate only resolves the audience, but the endpoint validates the whole notification
     * body - so an operator who picks a scope before writing the copy would get a 400 about the
     * message instead of a number. The text fields are filled in with a placeholder for this call
     * only; nothing is stored by it.
     */
    PREVIEWxAudience({
      ...payload,
      name: payload.name || "preview",
      header: payload.header || "preview",
      content: payload.content || "preview"
    });
  };

  const REVIEWxCampaign = () => {
    const errors = isCustomerMode ? validateNotificationForm(form) : validateForm(form);
    // Surface each error at its input and jump to the first before reviewing — both modes now,
    // so Custom (Firebase) no longer needs the separate summary box.
    if (!showFieldErrors()) {
      return;
    }
    if (errors.length === 0) {
      SETxReview(true);
      SETxStep(0);
    }
  };

  const PUBLISHxCampaign = () => {
    SETxShwConfirm(false);

    if (isCustomerMode) {
      /*
       * One call either way: the notification centre stores the record and, with sendNow, resolves
       * the audience and sends it in the same request. A scheduled one is stored with its time and
       * the service's own scheduler picks it up - this page does not have to come back for it.
       */
      SAVExNotification({
        id: notificationId,
        body: buildNotificationPayload(form, form.scheduleMode === "NOW")
      });
      return;
    }

    if (form.scheduleMode === "SCHEDULED") {
      // No scheduler on the backend yet — this only records a scheduled campaign
      saveCampaign(GENxCampaign("SCHEDULED"));
      SETxShwScheduled(true);
      return;
    }

    REQSendPushNoti({
      requests: oRequests,
      campaign: GENxCampaign("SENDING")
    });
  };

  // One tab per step: its number, its name and a one-line summary of what is filled in.
  const RENDxStepTab = stepDef => {
    const active = oStep === stepDef.no;
    const visited = oVisited.includes(stepDef.no);
    const summary = RENDxSummary(stepDef.no);
    return (
      <button
        type="button"
        role="tab"
        aria-selected={active}
        key={stepDef.no}
        title={summary}
        className={`push-noti-tab${active ? " active" : visited ? " visited" : ""}`}
        onClick={() => GOTOxStep(stepDef.no)}
      >
        <span className={`push-noti-step-circle${visited ? " visited" : ""}`}>
          {stepDef.no}
        </span>
        <span className="push-noti-tab-text">
          <span className="push-noti-step-title">{stepDef.title}</span>
          {/* Figma: "Optional" sits on its own line under the title (Custom-mode steps 4-5). */}
          {stepDef.optional && !readOnly ? (
            <span className="push-noti-step-optional">Optional</span>
          ) : null}
          {/* The per-step summary shows only when viewing a saved notification; the
              create / edit form keeps the step titles clean (just number + name). */}
          {readOnly ? <span className="push-noti-tab-summary">{summary}</span> : null}
        </span>
      </button>
    );
  };

  const RENDxStep1 = () => (
    <div className="push-noti-step-body">
      {/*
        * "Send to whom" selector hidden: only the In-app customer group mode is offered now
        * (Custom (Firebase) is commented out in DELIVERY_MODES), so the single-choice radio
        * row is redundant. deliveryMode stays at its "CUSTOMER" default. Restore this block
        * if a second delivery mode is re-enabled.
        */}
      {/*
      <div className="push-noti-field">
        <label className="push-noti-label">Send to whom</label>
        {DELIVERY_MODES.map(mode => (
          <label className="push-noti-radio" key={mode.value}>
            <input
              type="radio"
              checked={form.deliveryMode === mode.value}
              disabled={readOnly || Boolean(notificationId)}
              onChange={() => SETxValue("deliveryMode", mode.value)}
            />
            <span>
              {mode.label} — <span className="push-noti-mode-hint">{mode.hint}</span>
            </span>
          </label>
        ))}
      </div>
      */}

      <div className="push-noti-field">
        <label className="push-noti-label">
          Notification title <span className="push-noti-required">*</span>
        </label>
        <input
          id="pn-title"
          className={fieldClass("title", "push-noti-input")}
          maxLength={200}
          placeholder="PRINCIPAL TH"
          disabled={readOnly}
          value={form.title}
          onChange={SETxField("title")}
        />
        {RENDxFieldError("title")}
      </div>

      <div className="push-noti-field">
        <label className="push-noti-label">
          Notification message <span className="push-noti-required">*</span>
        </label>
        <textarea
          id="pn-body"
          className={fieldClass("body", "push-noti-textarea")}
          rows={4}
          maxLength={MESSAGE_MAX}
          placeholder="Message shown on the user's device"
          disabled={readOnly}
          value={form.body}
          onChange={SETxField("body")}
        />
        <p className="push-noti-hint push-noti-field-counter">
          {(form.body || "").length} / {MESSAGE_MAX}
        </p>
        {RENDxFieldError("body")}
      </div>

      <div className="push-noti-field">
        <label className="push-noti-label">Notification image (URL)</label>
        <input
          id="pn-imageUrl"
          className={fieldClass("imageUrl", "push-noti-input")}
          placeholder="https://..."
          disabled={readOnly}
          value={form.imageUrl}
          onChange={SETxField("imageUrl")}
        />
        {RENDxFieldError("imageUrl")}
        <p className="push-noti-hint">
          Recommended ≤ 1 MB, 2:1 ratio (e.g. 1024×512). Android caps images at 1 MB, iOS at ~10 MB.
        </p>
        {isCustomerMode ? null : (
          <p className="push-noti-hint">
            The backend does not support <code>notification.image</code> yet — this URL is sent in{" "}
            <code>data.image</code> for the app to render itself
          </p>
        )}
      </div>

      <div className="push-noti-field">
        <label className="push-noti-label">
          Notification name (internal) <span className="push-noti-required">*</span>
        </label>
        <input
          id="pn-campaignName"
          className={fieldClass("campaignName", "push-noti-input")}
          placeholder="e.g. promo-dec-2026"
          disabled={readOnly}
          value={form.campaignName}
          onChange={SETxField("campaignName")}
        />
        {RENDxFieldError("campaignName")}
      </div>

      {/* Everything below is stored on the notification itself, so it is only asked for when the
          notification centre is the one sending. */}
      {isCustomerMode ? (
        <Fragment>
          <div className="push-noti-field">
            <label className="push-noti-label">Status</label>
            <FormSelect
              value={form.status}
              options={STATUS_OPTIONS}
              onChange={value => SETxValue("status", value)}
              disabled={readOnly}
            />
          </div>

          <div className="push-noti-field">
            <label className="push-noti-label">Content type</label>
            <FormSelect
              value={form.contentType}
              options={CONTENT_TYPE_OPTIONS}
              onChange={onContentTypeChange}
              disabled={readOnly}
            />
          </div>

          <div className="push-noti-field">
            <label className="push-noti-label">Notification format</label>
            <FormSelect
              value={form.notificationType}
              options={NOTIFICATION_TYPE_OPTIONS}
              onChange={value => SETxValue("notificationType", value)}
              disabled={readOnly}
            />
            <p className="push-noti-hint">
              Silent only lands in the inbox and does not pop up on the user's device
            </p>
          </div>

          <div className="push-noti-field">
            <label className="push-noti-label">Button in the message</label>
            <FormSelect
              value={form.actionType}
              options={
                form.contentType === "SURVEY"
                  ? ACTION_TYPE_OPTIONS.filter(option => option.value === "SURVEY")
                  : ACTION_TYPE_OPTIONS.filter(option => option.value !== "SURVEY")
              }
              onChange={value => SETxValue("actionType", value)}
              disabled={readOnly || form.contentType === "SURVEY"}
            />
          </div>

          {form.actionType === "NONE" ? null : (
            <Fragment>
              {/* Figma order: the button label comes before its destination. The survey button
                  carries no label, so it is hidden for that action. */}
              {form.actionType === "SURVEY" ? null : (
                <div className="push-noti-field">
                  <label className="push-noti-label">Button label</label>
                  <input
                    id="pn-actionLabel"
                    className={fieldClass("actionLabel", "push-noti-input")}
                    disabled={readOnly}
                    maxLength={ACTION_LABEL_MAX}
                    placeholder="e.g. View details"
                    value={form.actionLabel}
                    onChange={SETxField("actionLabel")}
                  />
                  <p className="push-noti-hint push-noti-field-counter">
                    {(form.actionLabel || "").length} / {ACTION_LABEL_MAX}
                  </p>
                  {RENDxFieldError("actionLabel")}
                </div>
              )}

              <div className="push-noti-field">
                <label className="push-noti-label">
                  {form.actionType === "URL"
                    ? "Link (https)"
                    : form.actionType === "DEEPLINK"
                    ? "In-app page (deeplink)"
                    : "Survey"}
                </label>
                {form.actionType === "SURVEY" ? (
                  <FormSelect
                    inputId="pn-actionLink"
                    value={form.actionLink}
                    options={SURVEY_OPTIONS}
                    onChange={value => SETxValue("actionLink", value)}
                    disabled={readOnly}
                    placeholder="Select a survey"
                    invalid={Boolean(oFieldErrors.actionLink)}
                  />
                ) : (
                  <input
                    id="pn-actionLink"
                    className={fieldClass("actionLink", "push-noti-input")}
                    disabled={readOnly}
                    placeholder={form.actionType === "URL" ? "https://..." : "principalth://..."}
                    value={form.actionLink}
                    onChange={SETxField("actionLink")}
                  />
                )}
                {RENDxFieldError("actionLink")}
              </div>
            </Fragment>
          )}
        </Fragment>
      ) : null}

      <div className="push-noti-step-actions">
        {/* No Back on the first step; the spacer keeps Next on the right (Figma). */}
        <span />
        <button type="button" className="push-noti-next" onClick={() => tryGoNext(1, 2)}>
          Next
          <Icon name="arrowRight" />
        </button>
      </div>
    </div>
  );

  /*
   * Who the notification centre will resolve this to.
   *
   * The scope picks the population (every customer, MF holders, PVD members, people who have not
   * opened an account, or a list pasted in by email or customer uuid) and the optional demographic
   * filter narrows it. The estimate is the same resolution the send itself performs, run without
   * sending, so it is the real number rather than a guess.
   */
  const RENDxAudience = () => (
    <Fragment>
      <div className="push-noti-field">
        <label className="push-noti-label">User scope</label>
        <FormSelect
          value={form.userScope}
          options={USER_SCOPE_OPTIONS}
          onChange={value => SETxValue("userScope", value)}
          disabled={readOnly}
        />
      </div>

      {form.userScope === "EmailList" ? (
        <div className="push-noti-field">
          <label className="push-noti-label">Email (one per line)</label>
          <textarea
            id="pn-scopeEmails"
            className={fieldClass("scopeEmails", "push-noti-textarea")}
            rows={4}
            disabled={readOnly}
            placeholder={"someone@example.com\nanother@example.com"}
            value={form.scopeEmails}
            onChange={SETxField("scopeEmails")}
          />
          {RENDxFieldError("scopeEmails")}
          <div className="push-noti-csv">
            <label className={readOnly ? "push-noti-csv-upload disabled" : "push-noti-csv-upload"}>
              <Icon name="upload" />
              Upload CSV
              <input
                type="file"
                accept=".csv,text/csv"
                disabled={readOnly}
                onChange={IMPORTxCsv("scopeEmails")}
              />
            </label>
            <button
              type="button"
              className="push-noti-csv-sample"
              onClick={DOWNLOADxSample("sample-emails.csv", SAMPLE_EMAIL_CSV)}
            >
              <Icon name="download" />
              Download sample CSV
            </button>
          </div>
        </div>
      ) : null}

      {form.userScope === "CustomerList" ? (
        <div className="push-noti-field">
          <label className="push-noti-label">Customer uuid (one per line)</label>
          <textarea
            id="pn-scopeCustomerUuids"
            className={fieldClass("scopeCustomerUuids", "push-noti-textarea")}
            rows={4}
            disabled={readOnly}
            placeholder="404d1125-0e0c-4bba-883d-964d74fc731a"
            value={form.scopeCustomerUuids}
            onChange={SETxField("scopeCustomerUuids")}
          />
          {RENDxFieldError("scopeCustomerUuids")}
          <div className="push-noti-csv">
            <label className={readOnly ? "push-noti-csv-upload disabled" : "push-noti-csv-upload"}>
              <Icon name="upload" />
              Upload CSV
              <input
                type="file"
                accept=".csv,text/csv"
                disabled={readOnly}
                onChange={IMPORTxCsv("scopeCustomerUuids")}
              />
            </label>
            <button
              type="button"
              className="push-noti-csv-sample"
              onClick={DOWNLOADxSample("sample-customer-uuids.csv", SAMPLE_CUSTOMER_CSV)}
            >
              <Icon name="download" />
              Download sample CSV
            </button>
          </div>
        </div>
      ) : null}

      <div className="push-noti-field">
        <label className="push-noti-label">Estimated recipients</label>
        <div className="push-noti-audience">
          <button type="button" className="push-noti-back" onClick={PREVIEWxReach}>
            <Icon name="users" />
            Estimated recipients
          </button>
          <span className="push-noti-audience-count">
            {EstimatedReach === null || EstimatedReach === undefined
              ? "Not estimated yet"
              : `${EstimatedReach} people`}
          </span>
        </div>
      </div>
    </Fragment>
  );

  const RENDxStep2 = () => (
    <div className="push-noti-step-body">
      {isCustomerMode ? (
        <Fragment>
          {RENDxAudience()}
          <div className="push-noti-step-actions">
            <button type="button" className="push-noti-back" onClick={() => GOTOxStep(1)}>
              <Icon name="arrowLeft" />
              Back
            </button>
            <button type="button" className="push-noti-next" onClick={() => tryGoNext(2, 3)}>
              Next
              <Icon name="arrowRight" />
            </button>
          </div>
        </Fragment>
      ) : (
        RENDxFirebaseTarget()
      )}
    </div>
  );

  const RENDxFirebaseTarget = () => (
    <Fragment>
      <div className="push-noti-field">
        <label className="push-noti-label">Apps</label>
        <div className="push-noti-checkbox-row">
          <label className="push-noti-checkbox">
            <input
              type="checkbox"
              checked={form.platforms.ios}
              onChange={SETxPlatform("ios")}
              disabled={form.targetMode !== "ALL"}
            />
            <span>iOS</span>
          </label>
          <label className="push-noti-checkbox">
            <input
              type="checkbox"
              checked={form.platforms.android}
              onChange={SETxPlatform("android")}
              disabled={form.targetMode !== "ALL"}
            />
            <span>Android</span>
          </label>
        </div>
        <p className="push-noti-hint">
          The app subscribes to a topic per platform (<code>ios</code> / <code>android</code>) at login
        </p>
      </div>

      <div className="push-noti-field">
        <label className="push-noti-label">Target group</label>
        {TARGET_MODES.map(mode => (
          <label className="push-noti-radio" key={mode.value}>
            <input
              type="radio"
              checked={form.targetMode === mode.value}
              onChange={() => SETxValue("targetMode", mode.value)}
            />
            <span>{mode.label}</span>
          </label>
        ))}
      </div>

      {form.targetMode === "TOPIC" ? (
        <div className="push-noti-field">
          <label className="push-noti-label">Topic (one per line)</label>
          <textarea
            className="push-noti-textarea"
            rows={3}
            placeholder={"ios\nandroid"}
            value={form.topics}
            onChange={SETxField("topics")}
          />
        </div>
      ) : null}

      {form.targetMode === "USER" ? (
        <div className="push-noti-field">
          <label className="push-noti-label">
            customer uuid or unitholder id (one per line)
          </label>
          <textarea
            className="push-noti-textarea"
            rows={3}
            placeholder={"3f0c1f2e-....\n0123456789"}
            value={form.users}
            onChange={SETxField("users")}
          />
          <p className="push-noti-hint">
            The app subscribes to topics named after userId and unitholderId at login
          </p>
        </div>
      ) : null}

      {form.targetMode === "TOKEN" ? (
        <div className="push-noti-field">
          <label className="push-noti-label">FCM registration token (one per line)</label>
          <textarea
            className="push-noti-textarea"
            rows={4}
            placeholder="fMEP0vJqS0-..."
            value={form.tokens}
            onChange={SETxField("tokens")}
          />
        </div>
      ) : null}

      {oTargets.length === 0 ? RENDxFieldError("target") : null}

      <div className="push-noti-target-summary">
        The system will send <b>{oTargets.length}</b> time(s) (1 target per request, as the backend supports)
        {oTargets.length > 0 ? (
          <ul>
            {oTargets.slice(0, 10).map((target, index) => (
              <li key={`${target.kind}-${index}`}>{target.label}</li>
            ))}
            {oTargets.length > 10 ? <li>… and {oTargets.length - 10} more</li> : null}
          </ul>
        ) : null}
      </div>

      <div className="push-noti-step-actions">
        <button type="button" className="push-noti-back" onClick={() => GOTOxStep(1)}>
          <Icon name="arrowLeft" />
          Back
        </button>
        <button type="button" className="push-noti-next" onClick={() => tryGoNext(2, 3)}>
          Next
          <Icon name="arrowRight" />
        </button>
      </div>
    </Fragment>
  );

  const RENDxStep3 = () => (
    <div className="push-noti-step-body">
      <div className="push-noti-field">
        <label className="push-noti-label">Send time</label>
        <label className="push-noti-radio">
          <input
            type="radio"
            checked={form.scheduleMode === "NOW"}
            onChange={() => SETxValue("scheduleMode", "NOW")}
          />
          <span>Send now</span>
        </label>
        <label className="push-noti-radio">
          <input
            type="radio"
            checked={form.scheduleMode === "SCHEDULED"}
            onChange={() => SETxValue("scheduleMode", "SCHEDULED")}
          />
          <span>Schedule send</span>
        </label>
      </div>

      {form.scheduleMode === "SCHEDULED" ? (
        <Fragment>
          <div className="push-noti-field">
            <label className="push-noti-label">Date and time (Asia/Bangkok)</label>
            <span className="push-noti-datetime">
              <input
                id="pn-scheduledAt"
                type="date"
                className={fieldClass("scheduledAt", "push-noti-input")}
                disabled={readOnly}
                min={toDateInput(new Date())}
                value={toDateInput(form.scheduledAt)}
                onChange={event =>
                  SETxValue("scheduledAt", combineSchedule(event.target.value, toTimeInput(form.scheduledAt)))
                }
              />
              <input
                type="time"
                step={900}
                className={fieldClass("scheduledAt", "push-noti-input")}
                disabled={readOnly}
                value={toTimeInput(form.scheduledAt)}
                onChange={event =>
                  SETxValue(
                    "scheduledAt",
                    combineSchedule(toDateInput(form.scheduledAt) || toDateInput(new Date()), event.target.value)
                  )
                }
              />
            </span>
            {RENDxFieldError("scheduledAt")}
            <small className="push-noti-hint">
              Schedule the send at least {MIN_SCHEDULE_LEAD_MINUTES} minutes ahead so Firebase can
              spread the load and deliver on time.
            </small>
          </div>
          <div className="push-noti-warning">
            The backend has no send scheduler yet (POST /api/v1/push/notification always sends immediately).
            A scheduled campaign is only <b>saved as a pending item</b> on the Campaign page
            and will not be sent until the backend supports it.
          </div>
        </Fragment>
      ) : null}

      <div className="push-noti-step-actions">
        <button type="button" className="push-noti-back" onClick={() => GOTOxStep(2)}>
          <Icon name="arrowLeft" />
          Back
        </button>
        {isCustomerMode ? (
          <button type="button" className="push-noti-next" onClick={REVIEWxCampaign}>
            <Icon name="eye" />
            Review
          </button>
        ) : (
          <button type="button" className="push-noti-next" onClick={() => tryGoNext(3, 4)}>
            Next
            <Icon name="arrowRight" />
          </button>
        )}
      </div>
    </div>
  );

  const RENDxStep4 = () => (
    <div className="push-noti-step-body">
      <div className="push-noti-field">
        <label className="push-noti-label">Conversion event (optional)</label>
        <input
          className="push-noti-input"
          placeholder="e.g. first_open, purchase"
          value={form.conversionEvent}
          onChange={SETxField("conversionEvent")}
        />
        <p className="push-noti-hint">
          This value is sent in <code>data.conversion_event</code> for the app to fire analytics itself
          (not tied to Google Analytics like in the Firebase console)
        </p>
      </div>

      <div className="push-noti-step-actions">
        <button type="button" className="push-noti-back" onClick={() => GOTOxStep(3)}>
          <Icon name="arrowLeft" />
          Back
        </button>
        <button type="button" className="push-noti-next" onClick={() => GOTOxStep(5)}>
          Next
          <Icon name="arrowRight" />
        </button>
      </div>
    </div>
  );

  const RENDxStep5 = () => (
    <div className="push-noti-step-body">
      <div className="push-noti-field">
        <label className="push-noti-label">Data type (data.type)</label>
        <FormSelect
          value={form.dataType}
          options={DATA_TYPE_OPTIONS}
          onChange={value => SETxValue("dataType", value)}
        />
        <p className="push-noti-hint">
          The app recognises <code>qr_code</code> and <code>allotment</code> (RootRoute.tsx).
          Add other values yourself in the custom data table below.
        </p>
      </div>

      <div className="push-noti-field">
        <label className="push-noti-label">Destination link (data.deeplink)</label>
        <input
          className="push-noti-input"
          placeholder="principalth://..."
          value={form.deeplink}
          onChange={SETxField("deeplink")}
        />
      </div>

      <div className="push-noti-field-row">
        <div className="push-noti-field">
          <label className="push-noti-label">Priority (android.priority)</label>
          <FormSelect
            value={form.priority}
            options={PRIORITY_OPTIONS}
            onChange={value => SETxValue("priority", value)}
          />
        </div>

        <div className="push-noti-field">
          <label className="push-noti-label">Message lifetime (hours)</label>
          <input
            className="push-noti-input"
            type="number"
            min="0"
            max="672"
            placeholder="e.g. 24"
            value={form.ttlHours}
            onChange={SETxField("ttlHours")}
          />
          <p className="push-noti-hint">
            Sent as <code>android.ttl</code> in milliseconds (max 28 days)
          </p>
        </div>
      </div>

      <div className="push-noti-field-row">
        <div className="push-noti-field">
          <label className="push-noti-label">Android icon (android.notification.icon)</label>
          <input
            className="push-noti-input"
            placeholder="ic_notification"
            value={form.androidIcon}
            onChange={SETxField("androidIcon")}
          />
        </div>
        <div className="push-noti-field">
          <label className="push-noti-label">Android color (android.notification.color)</label>
          <input
            className="push-noti-input"
            placeholder="#0275b6"
            value={form.androidColor}
            onChange={SETxField("androidColor")}
          />
        </div>
      </div>

      <div className="push-noti-field-row">
        <div className="push-noti-field">
          <label className="push-noti-label">Android notification channel (data.channel_id)</label>
          <input
            className="push-noti-input"
            placeholder="default"
            value={form.androidChannelId}
            onChange={SETxField("androidChannelId")}
          />
        </div>
        <div className="push-noti-field">
          <label className="push-noti-label">iOS badge (data.badge)</label>
          <input
            className="push-noti-input"
            type="number"
            min="0"
            placeholder="1"
            value={form.iosBadge}
            onChange={SETxField("iosBadge")}
          />
        </div>
      </div>

      <div className="push-noti-field">
        <label className="push-noti-checkbox">
          <input
            type="checkbox"
            checked={form.sound}
            onChange={() => SETxValue("sound", !form.sound)}
          />
          <span>Enable sound (sends data.sound = false when off)</span>
        </label>
        <label className="push-noti-checkbox">
          <input
            type="checkbox"
            checked={form.contentAvailable}
            onChange={() => SETxValue("contentAvailable", !form.contentAvailable)}
          />
          <span>Wake the app in the background on iOS (apns.payload.aps.contentAvailable)</span>
        </label>
      </div>

      <div className="push-noti-field">
        <label className="push-noti-label">Additional data (custom data)</label>
        {form.customData.map((item, index) => (
          <div className="push-noti-kv-row" key={`kv-${index}`}>
            <input
              className="push-noti-input"
              placeholder="key"
              value={item.key}
              onChange={event => SETxCustomData(index, "key", event.target.value)}
            />
            <input
              className="push-noti-input"
              placeholder="value"
              value={item.value}
              onChange={event => SETxCustomData(index, "value", event.target.value)}
            />
            <button
              type="button"
              className="push-noti-kv-remove"
              onClick={() => DELxCustomData(index)}
            >
              <Icon name="trash" />
              Delete
            </button>
          </div>
        ))}
        <button type="button" className="push-noti-kv-add" onClick={ADDxCustomData}>
          <Icon name="plus" />
          Add data
        </button>
      </div>

      <div className="push-noti-step-actions">
        <button type="button" className="push-noti-back" onClick={() => GOTOxStep(4)}>
          <Icon name="arrowLeft" />
          Back
        </button>
        <button type="button" className="push-noti-next" onClick={REVIEWxCampaign}>
          <Icon name="eye" />
          Review
        </button>
      </div>
    </div>
  );

  const RENDxStepBody = stepNo => {
    if (stepNo === 1) return RENDxStep1();
    if (stepNo === 2) return RENDxStep2();
    if (stepNo === 3) return RENDxStep3();
    if (stepNo === 4) return RENDxStep4();
    return RENDxStep5();
  };

  const RENDxSummary = stepNo => {
    if (stepNo === 1) {
      return form.title || form.body ? `${form.title} — ${form.body}` : "Not filled in";
    }
    if (stepNo === 2) {
      if (!isCustomerMode) {
        return `${oTargets.length} target`;
      }
      const scope = USER_SCOPE_OPTIONS.find(option => option.value === form.userScope);
      const reach =
        EstimatedReach === null || EstimatedReach === undefined ? "" : ` · ~${EstimatedReach} people`;
      return `${scope ? scope.label : form.userScope}${reach}`;
    }
    if (stepNo === 3) {
      return form.scheduleMode === "NOW"
        ? "Send now"
        : `Scheduled ${form.scheduledAt ? new Date(form.scheduledAt).toLocaleString("th-TH") : "-"}`;
    }
    if (stepNo === 4) {
      return form.conversionEvent || "Unspecified";
    }
    return `${Object.keys(oRequests.length > 0 ? oRequests[0].payload.data : {}).length} data key`;
  };

  const optionLabel = (options, value) =>
    (options.find(option => option.value === value) || {}).label || value;

  const countLines = text =>
    new Set(
      (text || "")
        .split(/[\n,]+/)
        .map(item => item.trim())
        .filter(Boolean)
    ).size;

  // The "Target" cell on the review step: a scope summary for the notification centre,
  // or the per-request target list for a Firebase send.
  const RENDxReviewTarget = () => {
    if (!isCustomerMode) {
      return (
        <Fragment>
          {oTargets.length} request(s)
          {oTargets.length > 0 ? (
            <ul className="push-noti-review-list">
              {oTargets.slice(0, 8).map((target, index) => (
                <li key={`review-target-${index}`}>{target.label}</li>
              ))}
              {oTargets.length > 8 ? <li>… and {oTargets.length - 8} more</li> : null}
            </ul>
          ) : null}
        </Fragment>
      );
    }
    const listScope = form.userScope === "EmailList" || form.userScope === "CustomerList";
    const parts = [optionLabel(USER_SCOPE_OPTIONS, form.userScope)];
    if (!listScope && (form.scopeGender || form.scopeAgeMin || form.scopeAgeMax)) {
      parts.push(
        `Gender ${form.scopeGender || "any"} · Age ${form.scopeAgeMin || "any"}–${form.scopeAgeMax ||
          "any"}`
      );
    }
    if (form.userScope === "EmailList") {
      parts.push(`${countLines(form.scopeEmails)} email(s)`);
    } else if (form.userScope === "CustomerList") {
      parts.push(`${countLines(form.scopeCustomerUuids)} customer(s)`);
    }
    parts.push(
      EstimatedReach === null || EstimatedReach === undefined
        ? "Not estimated yet"
        : `~${EstimatedReach} people`
    );
    return parts.join(" · ");
  };

  // One [label, value, stepToEdit] per row of the review table, matching the Figma layout.
  const RENDxReviewRows = () => {
    const rows = [
      ["Send to whom", isCustomerMode ? "In-app customer group" : "Custom (Firebase)", 1],
      ["Notification name (internal)", form.campaignName || "—", 1],
      ["Notification title", form.title || "—", 1],
      ["Notification message", <span className="push-noti-review-prewrap">{form.body || "—"}</span>, 1],
      ["Notification image", form.imageUrl || "—", 1]
    ];
    if (isCustomerMode) {
      rows.push([
        "Status · Content type · Format",
        `${optionLabel(STATUS_OPTIONS, form.status)} · ${optionLabel(
          CONTENT_TYPE_OPTIONS,
          form.contentType
        )} · ${optionLabel(NOTIFICATION_TYPE_OPTIONS, form.notificationType)}`,
        1
      ]);
      rows.push([
        "Button in the message",
        form.actionType === "NONE"
          ? "None"
          : form.actionType === "SURVEY"
          ? `${optionLabel(ACTION_TYPE_OPTIONS, form.actionType)} · ${optionLabel(SURVEY_OPTIONS, form.actionLink)}`
          : `${optionLabel(ACTION_TYPE_OPTIONS, form.actionType)} · ${form.actionLabel} · ${form.actionLink}`,
        1
      ]);
    }
    rows.push(["Target", RENDxReviewTarget(), 2]);
    rows.push([
      "Scheduling",
      form.scheduleMode === "NOW"
        ? "Send now"
        : `${
            form.scheduledAt ? new Date(form.scheduledAt).toLocaleString("th-TH") : "-"
          } (Asia/Bangkok)`,
      3
    ]);
    if (!isCustomerMode) {
      rows.push(["Conversion event", form.conversionEvent || "Unspecified", 4]);
      rows.push([
        "Additional options",
        `${Object.keys(oRequests.length > 0 ? oRequests[0].payload.data : {}).length} data key`,
        5
      ]);
    }
    return rows;
  };

  return (
    <Fragment>
      {REQStrSendPushNoti || Saving ? (
        <div className="funds-list-progress">
          <CircularProgress />
          <p className="funds-list-progress-text">Sending notification...</p>
        </div>
      ) : null}

      <div className="push-noti-container" onMouseDown={NOxLabelTextSelection}>
        {/*
          * No heading here: the breadcrumb above already names the page, and the endpoint
          * this page posts to is configuration, not something an operator acts on.
          */}
        <div className="push-noti-topbar">
          <div className="push-noti-topbar-actions">
            {/*
              * Back to wherever this form was opened from. A notification-centre message is
              * reached from the list - there is no menu entry for this page - so leaving it
              * on the campaigns tab would strand the operator somewhere they never were.
              */}
            <button
              type="button"
              className="push-noti-ghost"
              onClick={requestCancel}
            >
              <Icon name="close" />
              Cancel
            </button>
            {readOnly ? null : (
              <button type="button" className="push-noti-ghost primary" onClick={SAVExDraft}>
                <Icon name="save" />
                {isCustomerMode && notificationId ? "Save changes" : "Save draft"}
              </button>
            )}
          </div>
        </div>

        {notificationError ? (
          <div className="push-noti-errors push-noti-banner">{notificationError}</div>
        ) : null}

        {readOnly ? (
          <div className="push-noti-warning push-noti-banner">
            Already sent — cannot be edited (Reach {Notification.reach} · View {Notification.view})
          </div>
        ) : null}

        <div className="push-noti-body">
          <div className="push-noti-steps">
            <div className="push-noti-tabs" role="tablist">
              {oSteps.map(RENDxStepTab)}
            </div>
            {/* Nothing is open while the review is showing (step 0) */}
            {oSteps.some(stepDef => stepDef.no === oStep) ? (
              <div className="push-noti-step push-noti-tab-panel" role="tabpanel">
                {RENDxStepBody(oStep)}
              </div>
            ) : null}

            {oReview ? (
              <div className="push-noti-review">
                <h3>Review before sending</h3>
                <table className="push-noti-review-table">
                  <tbody>
                    {RENDxReviewRows().map(([label, value, stepNo]) => (
                      <tr key={label}>
                        <th>{label}</th>
                        <td>{value || "—"}</td>
                        <td className="push-noti-review-edit-cell">
                          <button
                            type="button"
                            className="push-noti-review-edit"
                            onClick={() => GOTOxStep(stepNo)}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="push-noti-step-actions">
                  <button
                    type="button"
                    className="push-noti-back"
                    onClick={() => GOTOxStep(isCustomerMode ? 3 : 5)}
                  >
                    <Icon name="arrowLeft" />
                    Back
                  </button>
                  {readOnly ? null : (
                    <button
                      type="button"
                      className="push-noti-publish"
                      onClick={() => SETxShwConfirm(true)}
                    >
                      <Icon name={form.scheduleMode === "NOW" ? "send" : "save"} />
                      {form.scheduleMode === "NOW" ? "Send now" : "Save schedule"}
                    </button>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <div className="push-noti-side">
            <DevicePreview
              title={form.title}
              body={form.body}
              imageUrl={form.imageUrl}
              actionLabel={form.actionLabel}
            />

            <div className="push-noti-json">
              <button
                type="button"
                className="push-noti-json-toggle"
                onClick={() => SETxShwJson(!oShwJson)}
              >
                {oShwJson ? "Hide" : "View"} JSON to send ({oRequests.length} request)
              </button>
              {oShwJson ? (
                <pre className="push-noti-json-body">
                  {JSON.stringify(
                    oRequests.map(item => item.payload),
                    null,
                    2
                  )}
                </pre>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {oShwDiscard ? (
        <ComposeDialog
          title="Discard unsaved changes?"
          labelId="pn-discard-title"
          onDismiss={() => SETxShwDiscard(false)}
          actions={[
            { label: "Keep editing", autoFocus: true, onClick: () => SETxShwDiscard(false) },
            {
              label: "Discard changes",
              primary: true,
              onClick: () => {
                SETxShwDiscard(false);
                history.push("/Notification_List");
              }
            }
          ]}
        >
          Your unsaved edits will be discarded. Previously saved drafts are kept.
        </ComposeDialog>
      ) : null}

      {oShwConfirm ? (
        <ComposeDialog
          title={
            form.scheduleMode === "NOW"
              ? "Send this notification now?"
              : isCustomerMode
              ? "Save this scheduled notification?"
              : "Save this scheduled campaign?"
          }
          labelId="pn-confirm-title"
          onDismiss={() => SETxShwConfirm(false)}
          actions={[
            { label: "Cancel", autoFocus: true, onClick: () => SETxShwConfirm(false) },
            {
              label: form.scheduleMode === "NOW" ? "Send now" : "Save",
              primary: true,
              onClick: PUBLISHxCampaign
            }
          ]}
        >
          {form.scheduleMode === "NOW"
            ? isCustomerMode
              ? `It is sent to the ${optionLabel(
                  USER_SCOPE_OPTIONS,
                  form.userScope
                )} group right now and cannot be unsent.`
              : `It is sent to ${oTargets.length} Firebase target(s) right now and cannot be unsent.`
            : "It is saved as Pending and sent automatically at the scheduled time."}
        </ComposeDialog>
      ) : null}

      {oShwScheduled ? (
        <ComposeDialog
          title="Scheduled campaign saved"
          labelId="pn-scheduled-title"
          onDismiss={() => {
            SETxShwScheduled(false);
            history.push("/Notification_List");
          }}
          actions={[
            {
              label: "Go to list",
              primary: true,
              autoFocus: true,
              onClick: () => {
                SETxShwScheduled(false);
                history.push("/Notification_List");
              }
            }
          ]}
        >
          It is not sent yet — it will go out once the backend supports scheduling.
        </ComposeDialog>
      ) : null}

      {oShwResult ? (
        <ComposeDialog
          title={
            SendSummary
              ? `Sent ${SendSummary.succeeded} / ${SendSummary.total} targets`
              : "Send result"
          }
          labelId="pn-result-title"
          onDismiss={() => SETxShwResult(false)}
          actions={[
            {
              label: "Go to list",
              primary: true,
              autoFocus: true,
              onClick: () => {
                SETxShwResult(false);
                history.push("/Notification_List");
              }
            }
          ]}
        >
          {SendResults && SendResults.length > 0 ? (
            <span className="push-noti-result-list">
              {SendResults.map((item, index) => (
                <span
                  className={item.ok ? "push-noti-result ok" : "push-noti-result fail"}
                  key={`send-result-${index}`}
                >
                  {item.target.label}: {item.ok ? "Success" : `Failed (${item.message})`}
                </span>
              ))}
            </span>
          ) : null}
        </ComposeDialog>
      ) : null}
    </Fragment>
  );
}

const withConnect = connect(
  state => ({
    ...state.pushNotiComposeReducer,
    // The notification-centre half of this page. `errorMessage` is renamed because both slices
    // have one and the push slice's is already spread above.
    Notification: state.notificationDetailReducer.Notification,
    Saving: state.notificationDetailReducer.Saving,
    Saved: state.notificationDetailReducer.Saved,
    EstimatedReach: state.notificationDetailReducer.EstimatedReach,
    notificationError: state.notificationDetailReducer.errorMessage
  }),
  {
    REQSendPushNoti,
    CleanUpPushNoti,
    REQNotificationDetail,
    SAVExNotification,
    PREVIEWxAudience,
    CleanUpNotificationDetail
  }
);

export default compose(
  withConnect,
  withRouter
)(PushNotiCompose);
